import {
  Alert,
  Card,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { EditSquareIcon } from "../../ui/icons";
import DynamicSvgIcon from "../../ui/icons/DynamicSvgIcon";
import useOpenEditCategory from "../../../hooks/ui-states/useOpenEditCategory";

interface CategoriesListProps {
  categories: Category[];
  isLoading: boolean;
  error: Error | null;
  divider?: boolean;
  type: CategoryType;
}

const CategoriesList = ({ categories, isLoading, error, divider = false, type }: CategoriesListProps) => {
  const { t } = useTranslation('categories');
  const [, setOpenEditCategory] = useOpenEditCategory();

  const renderCategoryItem = (category: Category, divider: boolean = false): JSX.Element => {
    const childCategories = category.categories || [];
    const isRoot = category.isRoot;

    return (
      <>
        <ListItem
          divider={divider}
          secondaryAction={
            <IconButton
              edge="end"
              aria-label={t('edit_category', { ns: 'categories', name: category.name })}
              onClick={() => setOpenEditCategory(true, category.id, type)}
            >
              <EditSquareIcon style={{ fontSize: 16 }} />
            </IconButton>
          }
          sx={{
            pl: isRoot || type === 'income' ? 1 : 2,
            mt: isRoot ? 2 : 0,
          }}
        >
          <ListItemAvatar sx={{ minWidth: 36 }}>
            <Card
              elevation={isRoot ? 4 : 0}
              sx={{ borderRadius: '50%', overflow: 'hidden', display: 'inline-block' }}
            >
              <DynamicSvgIcon
                svgUrl={category.icon}
                color={category.iconColor}
                backgroundColor={category.backgroundColor}
                size={30}
                iconSize={20}
              />
            </Card>
          </ListItemAvatar>
          <ListItemText
            primary={category.name}
            secondary={category.type}
          />
        </ListItem>
        {childCategories.length > 0 &&
          childCategories.map((childCategory: Category) => (
            <div key={childCategory.id}>
              {renderCategoryItem(childCategory)}
            </div>
          ))
        }
      </>
    );
  };

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" minHeight="200px">
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          {t('loading_categories', { ns: 'categories' })}
        </Typography>
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {t('error_loading_categories', { ns: 'categories' })}
      </Alert>
    );
  }

  if (!categories || categories.length === 0) {
    return (
      <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
        {t('no_categories_found', { ns: 'categories' })}
      </Typography>
    );
  }

  return (
    <List sx={{ mt: type === 'income' ? 2 : 0 }}>
      {categories?.map((category) => (
        <div key={category.id}>
          {renderCategoryItem(category, divider)}
        </div>
      ))}
    </List>
  );
};

export default CategoriesList;
